/**
 * Section descriptor contract (#997 — Story 3.1, Epic 3 FR10/FR11).
 *
 * A `SectionDescriptor` declaratively binds a dynamic (repeatable or 1:1)
 * request section's FULL definition in one place: its typed fields, storage
 * cardinality, permission reference, Summary order, PDF layout, shared
 * request-level fields, attachments, Additional Information, an offline/REST
 * binding, and a DB binding for the descriptor-to-table guardrail.
 *
 * Story 3.1 proves the existing generic engine (`RepeatableInstancesSection`,
 * the Summary factory, the offline overlay + flush) can be driven purely from a
 * descriptor, against a SYNTHETIC section only. No live section is migrated here
 * (that is Story 3.3 + Epic 4). The field layer composes the existing
 * `RequestFieldDefinition` registry rows and ADDS `type` + `validation`, which
 * the registry deliberately omits (it is UI-metadata only).
 */

import type { RequestFieldDefinition } from '../fields/types';

/**
 * The input control a descriptor field renders. Maps 1:1 to a shared form
 * component in `descriptorEditFields.tsx`.
 */
export type SectionDescriptorFieldType =
  | 'text'
  | 'multilineText'
  | 'number'
  | 'dropdown'
  | 'yesNo'
  | 'checkbox'
  | 'locationTag';

/**
 * Declarative validation rule for a descriptor field. Kept intentionally small
 * (required / min / max / pattern / options) — the engine converts these into
 * inline field errors, so a value that fails is blocked from saving. Zod is not
 * used at module level here (iOS crash risk); the engine builds validators
 * lazily from these primitives.
 */
export interface SectionDescriptorFieldValidation {
  required?: boolean;
  /** Numeric bounds (for `number` fields). */
  min?: number;
  max?: number;
  /** Allowed values (for `dropdown` fields). */
  options?: readonly string[];
  /** Regex source string (compiled lazily) + human message. */
  pattern?: { source: string; message: string };
  /** Max length (for `text` / `multilineText`). */
  maxLength?: number;
}

/**
 * A descriptor field = a registry `RequestFieldDefinition` (dataField, stepId,
 * subsectionId, order, label, shortLabel, surfaces) PLUS the render `type` and
 * optional `validation` the registry does not carry.
 */
export interface SectionDescriptorField extends RequestFieldDefinition {
  type: SectionDescriptorFieldType;
  validation?: SectionDescriptorFieldValidation;
  /** Dropdown option list when `type === 'dropdown'` (mirrors validation.options). */
  options?: readonly string[];
  placeholder?: string;
}

/** Shared per-unit Location descriptor used by all six repeatable sections. */
export function locationTagDescriptorField(
  stepId: RequestFieldDefinition['stepId'],
  subsectionId: string,
  order = 0,
): SectionDescriptorField {
  return {
    dataField: 'locationTagId',
    stepId,
    subsectionId,
    order,
    label: 'Location',
    shortLabel: 'Location',
    surfaces: { step: true, summary: true },
    type: 'locationTag',
  };
}

/** How many rows a section owns: a repeatable list (`many`) or a single row (`one`). */
export type SectionCardinality = 'one' | 'many';

/**
 * Editor card layout (#997 — Story 6.3). Partitions the per-unit fields across
 * the editor's attachment cards, so each field renders in its correct card
 * instead of all collapsing into the main card. Each entry binds one attachment
 * base section to the field-group (`subsectionId`) keys whose fields it shows.
 *
 * Optional on the descriptor: when absent, the edit body renders every per-unit
 * field in the single main card (pre-6.3 behavior, kept for synthetic/other
 * descriptors). The Additional Information card is NOT declared here — it is
 * owned separately by `additionalInfoField`.
 */
export interface SectionEditorCard {
  /** Attachment base section id this card owns (must be in `attachmentBaseSectionIds`). */
  baseSectionId: string;
  /** Card title. */
  title: string;
  /** Field-group (`subsectionId`) keys whose per-unit fields render in this card. */
  fieldGroupKeys: readonly string[];
}

/** Engine list layouts (mirrors `RepeatableTableLayout`, kept local to avoid a components import). */
export type SectionListLayout = 'desktop' | 'compact';

/**
 * Descriptor-declared list projection (#997 — Story 6.3): the table the engine
 * list renders for this section. Each section's list columns were product-approved
 * individually (hand-authored per section, like the pre-engine bespoke lists), so
 * the descriptor DECLARES its projection and the engine renders it — the
 * convention for every 6-4+ peer migration. Rows arrive ENGINE-shaped
 * (identity as `instanceName`/`instanceStatus`); a section whose formatters
 * expect its persisted shape adapts inside its binding (parity by reuse).
 *
 * Optional on the descriptor: when absent, the engine falls back to the generic
 * first-3-per-unit-fields table (synthetic descriptors, pre-migration peers).
 */
export interface SectionListColumnsProjection {
  /** Projection-owned location-tag placement; avoids global column-name overrides. */
  locationTagCellColumn?: string;
  locationTagSublineColumn?: string;
  columnsForLayout: (layout: SectionListLayout) => readonly string[];
  headerLabel: (column: string, layout: SectionListLayout) => string;
  formatCell: (
    row: Record<string, unknown>,
    column: string,
    layout: SectionListLayout,
  ) => string;
  columnAlign?: Record<string, 'left' | 'center'>;
  getColumnSubline?: (
    row: Record<string, unknown>,
    column: string,
    layout: SectionListLayout,
  ) => string | undefined;
  columnUsesDoubleLine?: (column: string, layout: SectionListLayout) => boolean;
}

/**
 * Permission reference: the section resolves RBAC through the existing
 * dataField→fieldMapping bridge (`resolveRbacKeysForDataField`). `rbacAnchorDataField`
 * is the anchor field whose permission cell governs the section (Racks pattern).
 */
export interface SectionPermissionRef {
  /** dataField whose fieldMapping cell anchors the section's RBAC. */
  rbacAnchorDataField: string;
}

/**
 * PDF layout binding: the per-unit anchor's owning stepId plus the schema
 * field-group keys that hold the section's per-unit labels. Mirrors the shared
 * `PDF_PER_UNIT_ANCHOR_TO_STEP_ID` + projection-local `PER_UNIT_FIELD_GROUPS`.
 */
export interface SectionPdfLayout {
  /** Owning RequestFormStepId (as a string, to avoid a server import in the type). */
  stepId: string;
  /** Per-unit anchor heading text (docxtemplater loop heading). */
  anchor: string;
  /** Schema field-group keys carrying the per-unit labels. */
  perUnitFieldGroupKeys: readonly string[];
}

/**
 * Offline + REST binding. The descriptor owns its own flush (NFR6/R7), so it
 * carries the REST resource segment and the reorder body key the flush uses to
 * build POST/PUT/DELETE/PATCH-reorder calls, plus the cache-array key and the
 * op-field selectors the generic overlay needs.
 */
export interface SectionOfflineBinding {
  /**
   * REST path segment under `/requests/:requestId/<restResource>` for
   * create/update/delete, and `/reorder` for reorder.
   */
  restResource: string;
  /**
   * Explicit opt-in to the descriptor engine's delegating save/flush path
   * (#997 — Story 6.4, section #2 generalization). Replaces the earlier
   * `restResource === 'racks'` magic-string gate: a section sets this `true`
   * once it has a proven delegating flush shim wired into
   * `resolveDelegatedFlushBinding` (Racks + Small UPS today). Descriptors
   * without a shim omit it and keep the pre-6.2 behavior (offline-cache
   * fallback, no create-time file move). Read by `descriptorSupportsDelegatedFlush`.
   */
  delegatedFlush?: boolean;
  /** Body key for the reorder PATCH payload, e.g. `largeUpsIds`. */
  reorderIdsBodyKey: string;
  /** Key on the cached request holding the persisted instance array. */
  persistedInstancesKey: string;
  /** Per-op field name carrying the instance id, e.g. `largeUpsId`. */
  instanceIdOpField: string;
  /** Per-op field name carrying the reorder id list, e.g. `largeUpsIds`. */
  reorderIdsOpField: string;
}

/**
 * DB binding for the descriptor-to-table guardrail (`descriptorDbBinding.test.ts`).
 * `columns` are the snake_case DB columns the section's persisted fields write to
 * — the test asserts they match the migration table columns AND the mapper keys.
 */
export interface SectionDbBinding {
  table: string;
  columns: readonly string[];
}

/**
 * The full declarative definition of a dynamic section. One descriptor drives
 * the editor, Summary, offline sync + flush, PDF hooks, and the guardrails.
 */
export interface SectionDescriptor {
  /** Stable section id (the RepeatableInstancesSection `sectionId`). */
  id: string;
  /** RequestFormStepId this section belongs to (string to avoid server import). */
  stepId: string;
  /**
   * Human labels for the editor list/empty state/buttons. The optional entries
   * override the adapter's `descriptorEngineLabels` templates per-label (#997
   * Story 6.3 R5 / GATE-5) so a section with hand-authored chrome copy (Racks)
   * reaches byte parity; descriptors that omit them keep today's templates.
   */
  labels: {
    entitySingular: string;
    entityPlural: string;
    addButtonLabel: string;
    emptyStateTitle: string;
    editFormTitle: string;
    editFormSubtitle?: string;
    editSaveLabel?: string;
    editSaveDraftLabel?: string;
    editDeleteLabel?: string;
    deleteConfirmTitle?: string;
    deleteConfirmMessage?: string;
    editAllListLabel?: string;
    /** Editor-screen discard dialog copy (dirty dismiss). */
    editDiscardTitle?: string;
    editDiscardMessage?: string;
    editDiscardConfirmLabel?: string;
    editDiscardKeepEditingLabel?: string;
    /** Edit-chrome title for an unnamed NEW unit (e.g. 'New rack'). */
    newInstanceChromeTitle?: string;
  };
  cardinality: SectionCardinality;
  /** Ordered, typed fields (editor + summary source of truth). */
  fields: readonly SectionDescriptorField[];
  permissionRef: SectionPermissionRef;
  /** dataField keys, in Summary display order. */
  summaryOrder: readonly string[];
  pdfLayout: SectionPdfLayout;
  /** Request-level (shared) field keys the editor exposes but does not own per-unit. */
  sharedFieldKeys: readonly string[];
  attachmentsEnabled: boolean;
  /**
   * Per-card attachment base section ids (one per attachment surface the editor
   * shows). Resolved per unit via `resolveRepeatableInstanceAttachmentSectionId`.
   * Optional so existing/synthetic descriptors that rely on a single hard-coded
   * base section stay valid; Racks declares its three cards here.
   */
  attachmentBaseSectionIds?: readonly string[];
  /**
   * Per-card field partition for the editor (#997 — Story 6.3). One entry per
   * field-card (in render order), mapping an attachment base section to the
   * `subsectionId` groups it shows. Omit to render all per-unit fields in one card.
   */
  editorCards?: readonly SectionEditorCard[];
  /**
   * List table projection (#997 — Story 6.3). Declares the section's
   * product-approved list columns/formatting; omit for the generic
   * first-3-fields fallback table.
   */
  listColumns?: SectionListColumnsProjection;
  /** dataField of the free-text Additional Information field, if any. */
  additionalInfoField?: string;
  offlineBinding: SectionOfflineBinding;
  dbBinding: SectionDbBinding;
}

/** The editor-owned (per-unit, persisted) fields: not shared, not attachments. */
export function descriptorPerUnitFields(
  descriptor: SectionDescriptor,
): readonly SectionDescriptorField[] {
  const shared = new Set(descriptor.sharedFieldKeys);
  return descriptor.fields.filter(field => !shared.has(field.dataField));
}

/** The shared request-level fields the editor exposes, in descriptor order. */
export function descriptorSharedFields(
  descriptor: SectionDescriptor,
): readonly SectionDescriptorField[] {
  const shared = new Set(descriptor.sharedFieldKeys);
  return descriptor.fields.filter(field => shared.has(field.dataField));
}

/**
 * Per-unit fields whose `subsectionId` is in `fieldGroupKeys`, in descriptor
 * order (#997 — Story 6.3 editor-card partition). Excludes shared fields.
 */
export function descriptorFieldsInGroups(
  descriptor: SectionDescriptor,
  fieldGroupKeys: readonly string[],
): readonly SectionDescriptorField[] {
  const groups = new Set(fieldGroupKeys);
  return descriptorPerUnitFields(descriptor).filter(field =>
    groups.has(field.subsectionId),
  );
}
